import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOnMountUnsafe } from '@make-software/tg-mini-app-ui';

import {
  fetchActivity,
  selectActivityItems,
  selectActivityLoading,
  selectActivityError,
  selectActivityCount,
  useSeasonInfo,
  useMe,
  AppDispatch
} from '@/shared';
import { VoteTransactionActivity } from '@/entities';

const PAGE_STEP = 5;
const nowSeconds = () => Math.floor(Date.now() / 1000);

export type DecoratedVoteActivity = VoteTransactionActivity & {
  isPending: boolean;
  isExpired: boolean;
};

export const useScreen = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: airdropInfo } = useSeasonInfo();
  const meService = useMe();

  const items = useSelector(selectActivityItems);
  const loading = useSelector(selectActivityLoading);
  const error = useSelector(selectActivityError);
  const count = useSelector(selectActivityCount);

  useOnMountUnsafe(async () => {
    await meService.refresh();
    await dispatch(fetchActivity({ page: 1, pageSize: PAGE_STEP }));
  });

  const hasMore = items.length < count;

  const loadMore = React.useCallback(() => {
    if (loading || !hasMore) return;

    const nextSize = Math.min(items.length + PAGE_STEP, count || items.length + PAGE_STEP);
    dispatch(fetchActivity({ page: 1, pageSize: nextSize }));
  }, [dispatch, loading, hasMore, items.length, count]);

  const refresh = React.useCallback(async () => {
    if (loading) return;

    const size = Math.max(items.length || PAGE_STEP, PAGE_STEP);

    await meService.refresh();
    await dispatch(fetchActivity({ page: 1, pageSize: size }));
  }, [loading, items.length, meService, dispatch]);

  const { round1Items, round2Items } = useMemo(() => {
    const now = nowSeconds();

    const decorate = (v: VoteTransactionActivity): DecoratedVoteActivity => {
      const isPending = v.expiration > now && !v.refunded && v.processed_at == null;
      const isExpired = v.expiration < now && v.refunded && v.processed_at == null;

      return { ...v, isPending, isExpired };
    };

    const decorated = items.map(decorate);

    return {
      round1Items: decorated.filter((ac) => ac.round === 1),
      round2Items: decorated.filter((ac) => ac.round === 2)
    };
  }, [items]);

  return {
    round1Items,
    round2Items,

    loading,
    error,
    count,
    hasMore,
    loadMore,
    refresh,
    airdropInfo
  };
};
